import processResponseResults from './processResponseResults';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import store from '../../store/store';
import { getSuggestionsService } from '../people/getSuggestionsService';
import updateFindResultTransactionId from '../updateFindResultTransactionId';
import { now } from 'owa-datetime';
import { populateFeedbackStateNetwork } from 'owa-readwrite-recipient-well-fabric/lib/utils/populateFeedbackState';
import processSubstrateSuggestionsResponse from 'owa-readwrite-recipient-well/lib/actions/processSubstrateSuggestionsResponse';
import addToCache from 'owa-recipient-cache/lib/actions/addToCache';
import FindControlViewState from 'owa-recipient-common/lib/store/schema/FindControlViewState';
import FindRecipientPersonaType from 'owa-recipient-common/lib/store/schema/FindRecipientPersonaType';
import addTransactionComplete from 'owa-recipient-common/lib/utils/addTransactionComplete';
import { SubstrateSearchSuggestionsResponse } from 'owa-search-service';
import shouldUse3SPeopleSuggestions from 'owa-substrate-people-suggestions/lib/utils/shouldUse3SPeopleSuggestions';
import FindPeopleResponseMessage from 'owa-service/lib/contract/FindPeopleResponseMessage';
import PersonaType from 'owa-service/lib/contract/PersonaType';
import { action } from 'satcheljs/lib/legacy';

const MAX_ENTRIES_TYPEDOWN = 5;

export default action('backfillCache')(async function backfillCache(
    recipientWell: FindControlViewState | RecipientWellWithFindControlViewState,
    queryString: string
): Promise<void> {
    store.pendingFindPeopleRequests++;
    let suggestionService = getSuggestionsService();

    let transactionStartTime = now();

    // Record the beginning of the live call
    // in case the live call hangs and the user submits feedback before it returns
    populateFeedbackStateNetwork(recipientWell, transactionStartTime, null);
    return suggestionService(
        queryString,
        false /*searchDirectory*/,
        false /*includeFeatureData*/
    ).then(serverResults => {
        const resultSet = getPersonaResults(serverResults);

        // Backfill the cache with server results
        addToCache(resultSet);
        store.pendingFindPeopleRequests--;

        // If there are server results, use the server results
        if (!isServerResultEmpty(serverResults) && recipientWell.queryString == queryString) {
            const hasCacheResults = recipientWell.findResultSet.length > 0;
            populateFeedbackStateNetwork(recipientWell, transactionStartTime, serverResults);

            if (hasCacheResults) {
                // If there are cache results, remove the duplicate results from the live results
                const cacheResultEmailAddresses = new Map<string, FindRecipientPersonaType>();
                recipientWell.findResultSet.forEach(value => {
                    cacheResultEmailAddresses[value.EmailAddress.EmailAddress] = value;
                });
                removeDupilicateResultsFromResponse(cacheResultEmailAddresses, serverResults);
            }

            // Process the response results to update the UI. If the UI already has cache results for this query, this will append the server results to the end of the list.
            // Otherwise it will clear out the existing find result set (from a previous query) and replace with the live results
            processResponseResults(
                recipientWell.findResultSet,
                serverResults,
                hasCacheResults,
                MAX_ENTRIES_TYPEDOWN
            );
            updateFindResultTransactionId(recipientWell, serverResults);
            addTransactionComplete(recipientWell, transactionStartTime);
        }
    });
});

export function getPersonaResults(
    serverResult: FindPeopleResponseMessage | SubstrateSearchSuggestionsResponse
): PersonaType[] {
    if (shouldUse3SPeopleSuggestions()) {
        let personas = [];
        processSubstrateSuggestionsResponse(
            personas,
            serverResult as SubstrateSearchSuggestionsResponse,
            false
        );
        return personas;
    } else {
        return (serverResult as FindPeopleResponseMessage).ResultSet;
    }
}

function isServerResultEmpty(
    serverResult: FindPeopleResponseMessage | SubstrateSearchSuggestionsResponse
): boolean {
    if (shouldUse3SPeopleSuggestions()) {
        const result = serverResult as SubstrateSearchSuggestionsResponse;
        return !result || !result.Groups;
    } else {
        const result = serverResult as FindPeopleResponseMessage;
        return result.ResponseClass != 'Success' || result.ResultSet.length == 0;
    }
}

function removeDupilicateResultsFromResponse(
    currentEmailAddresses: Map<string, FindRecipientPersonaType>,
    serverResult: FindPeopleResponseMessage | SubstrateSearchSuggestionsResponse
): void {
    if (shouldUse3SPeopleSuggestions()) {
        removeDuplicate3SResults(
            currentEmailAddresses,
            serverResult as SubstrateSearchSuggestionsResponse
        );
    } else {
        removeDuplicateFindPeopleResults(
            currentEmailAddresses,
            serverResult as FindPeopleResponseMessage
        );
    }
}

function removeDuplicate3SResults(
    currentEmailAddresses: Map<string, FindRecipientPersonaType>,
    resp: SubstrateSearchSuggestionsResponse
) {
    for (const group of resp.Groups) {
        if (group.Type === 'People') {
            let filteredSuggestions = group.Suggestions.filter(value => {
                return (
                    currentEmailAddresses[value.EmailAddresses ? value.EmailAddresses[0] : ''] ==
                    null
                );
            });
            group.Suggestions = filteredSuggestions;
            break;
        }
    }
}

function removeDuplicateFindPeopleResults(
    currentEmailAddresses: Map<string, FindRecipientPersonaType>,
    resp: FindPeopleResponseMessage
) {
    let filteredSuggestions = resp.ResultSet.filter(value => {
        return (
            currentEmailAddresses[value.EmailAddress ? value.EmailAddress.EmailAddress : ''] == null
        );
    });
    resp.ResultSet = filteredSuggestions;
}
